import React, { useState, useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { PaymentGatewayProvider } from './components/context/PaymentGatewayContext';
// Layout
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import PromoRibbon from './components/PromoRibbon';
import FloatingActions from './components/FloatingActions';
import LoadingScreen from './components/LoadingScreen';
import PromotionalModal from './components/PromotionalModal';

// Context Providers
import { ReviewProvider } from './components/context/ReviewContext';
import { CategoriesProvider } from './components/context/CategoriesContext';
import { ProductProvider } from './components/context/ProductContext';
import { FaqProvider } from './components/context/FaqContext';
import { AuthProvider } from './components/context/AuthContext';
import { ProfileProvider } from './components/context/ProfileContext';

// Protected Route
import ProtectedRoute from './components/config/ProtectedRoute';

// Pages — lazy-loaded so each route ships in its own chunk instead of the
// initial bundle (home visitors no longer download Cart/Orders/Auth/etc.).
const HomePage = lazy(() => import('./components/HomePage'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const Wishlist = lazy(() => import('./components/Wishlist/Wishlist'));
const Cart = lazy(() => import('./components/Cart/Cart'));
const OrderHistory = lazy(() => import('./components/Orders/OrderHistory'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const SignUp = lazy(() => import('./components/Auth/SignUp'));
const SignIn = lazy(() => import('./components/Auth/Signln'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ProductListing = lazy(() => import('./components/ProductDetails/ProductListing'));
const Products = lazy(() => import('./components/Products/Products'));
const FilteredProducts = lazy(() => import('./components/FilteredProducts/FilteredProducts'));
const BlogPage = lazy(() => import('./components/Policies/Blog'));
const BlogPost = lazy(() => import('./components/Policies/BlogPost'));
const NotFound = lazy(() => import('./components/NotFound'));
const Report = lazy(() => import('./components/Settings/Report.jsx'));

// Policies
const Terms = lazy(() => import('./components/Policies/Terms'));
const Contactus = lazy(() => import('./components/Policies/Contactus'));
const AboutUs = lazy(() => import('./components/Policies/AboutUs'));
const Faqs = lazy(() => import('./components/Policies/Faqs'));
const PrivacyPolicy = lazy(() => import('./components/Policies/PrivacyPolicy'));
const PaymentPolicy = lazy(() => import('./components/Policies/PaymentPolicy'));
const RefundReturnPolicy = lazy(() => import('./components/Policies/RefundReturnPolicy'));
const Cookies = lazy(() => import('./components/Policies/Cookie.jsx'));

/* Lightweight fallback shown while a lazy route chunk loads */
const RouteFallback = () => (
  <div className="flex items-center justify-center py-32">
    <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

/* =========================
   Page Transition Wrapper
========================= */
const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3, ease: 'easeInOut' }}
  >
    {children}
  </motion.div>
);

/* =========================
   Animated Routes
========================= */
const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<RouteFallback />}>
      <Routes location={location} key={location.pathname}>

        {/* PUBLIC ROUTES */}
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/signin" element={<PageTransition><SignIn /></PageTransition>} />
        <Route path="/signup" element={<PageTransition><SignUp /></PageTransition>} />
        <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />

        <Route path="/products" element={<PageTransition><Products /></PageTransition>} />
        <Route path="/shop" element={<PageTransition><FilteredProducts /></PageTransition>} />
        {/* Legacy redirect support */}
        <Route path="/products/category/:categoryId" element={<PageTransition><FilteredProducts /></PageTransition>} />
        {/* :slug can be the admin url_slug or a numeric id — ProductListing resolves both */}
        <Route path="/product/:slug" element={<PageTransition><ProductListing /></PageTransition>} />

        <Route path="/about" element={<PageTransition><AboutUs /></PageTransition>} />
        <Route path="/terms&conditions" element={<PageTransition><Terms /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contactus /></PageTransition>} />
        <Route path="/faq" element={<PageTransition><Faqs /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><BlogPost /></PageTransition>} />

        <Route path="/cookie-policy" element={<Cookies />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/payment-policy" element={<PaymentPolicy />} />
        <Route path="/refund-policy" element={<RefundReturnPolicy />} />

        {/* PROTECTED ROUTES */}
        <Route element={<ProtectedRoute />}>
          <Route path="/profile" element={<PageTransition><Profile /></PageTransition>} />
          <Route path="/wishlist" element={<PageTransition><Wishlist /></PageTransition>} />
          <Route path="/cart" element={<PageTransition><Cart /></PageTransition>} />
          <Route path="/orders" element={<PageTransition><OrderHistory /></PageTransition>} />
          <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
          <Route path="/report" element={<PageTransition><Report /></PageTransition>} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />

      </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

/* =========================
   App Root
========================= */
function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPromotionalModal, setShowPromotionalModal] = useState(false);

  useEffect(() => {
    const navEntries = performance.getEntriesByType("navigation");
    const isReload =
      navEntries.length > 0 && navEntries[0].type === "reload";

    if (isReload) {
      setIsLoading(true);
    }
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);

    // Show promotional modal after loading completes
    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      setShowPromotionalModal(true);
    }, 500);
  };

  const handleClosePromotionalModal = () => {
    setShowPromotionalModal(false);
  };

  if (isLoading) {
    return <LoadingScreen onLoadingComplete={handleLoadingComplete} />;
  }

  return (
    <BrowserRouter basename="/">
      <AuthProvider>
           <PaymentGatewayProvider>
          <ScrollToTop />
          <ReviewProvider>
            <CategoriesProvider>

              <ProductProvider>
                <FaqProvider>
                  <ProfileProvider>

                    <div className="App bg-orange-50 bg-opacity-90 min-h-screen flex flex-col">
                      <div className="sticky top-0 z-50">
                        <PromoRibbon />
                        <Header />
                      </div>

                      <main className="pt-0 flex-grow">
                        <AnimatedRoutes />
                      </main>

                      <Footer />
                      <FloatingActions />

                      <Toaster
                        position="top-right"
                        containerStyle={{
                          top: '120px',
                          right: '16px',
                          zIndex: 9999,
                        }}
                      />

                      {/* Promotional Modal */}
                      <PromotionalModal
                        isVisible={showPromotionalModal}
                        onClose={handleClosePromotionalModal}
                      />

                    </div>

                  </ProfileProvider>
                </FaqProvider>
              </ProductProvider>

            </CategoriesProvider>
          </ReviewProvider>
          </PaymentGatewayProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
